import {useEffect, useRef, useState} from "react";
import SharedService from "@/services/shared-service";
import {getDailyEvents} from "@/services/home-service";
import {LEVEL_CLERGY_MAP} from "@/configuration/static-data-provider";
import moment from "moment/moment";
import Link from "next/link";
import {TbGrave} from "react-icons/tb";
import {LiaBirthdayCakeSolid} from "react-icons/lia";
import {GiHeartWings, GiPartyPopper} from "react-icons/gi";
import {MdEvent} from "react-icons/md";
import {ScrollShadow} from "@nextui-org/react";

export default function EventsBlock({initialData = null}) {
    const baseDate = SharedService.moment();
    const [selectedDate, setSelectedDate] = useState(baseDate.clone());
    const [detail, setDetail] = useState({
        today: null,
        description: null,
        birth: null,
        priest: null,
        saint: null,
        masses: null,
        scheduleEvents: null,
        rip: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const todayRef = useRef(null);

    const days = Array.from({length: 7}, (_, i) =>
        baseDate.clone().add(i - 3, "day")
    );

    async function renderComponentByDate(date) {
        setIsLoading(true);
        try {
            const dailyEvents = await getDailyEvents(date);

            setDetail({
                today: date.format("DD-MM-YYYY"),
                rip: dailyEvents.rip.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                birth: dailyEvents.birth.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                priest: dailyEvents.priest.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                saint: dailyEvents.saint.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                masses: dailyEvents.masses,
                description: dailyEvents.masses.length > 0 ? dailyEvents.masses[0].description : null,
                scheduleEvents: dailyEvents.scheduleEvents
            });
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (initialData) {
            setDetail(initialData);
        } else {
            renderComponentByDate();
        }

        if (todayRef.current) {
            todayRef.current.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, []);

    const hasEvents = detail.masses?.length > 0 ||
        detail.scheduleEvents?.length > 0 ||
        detail.birth?.length > 0 ||
        detail.priest?.length > 0 ||
        detail.saint?.length > 0 ||
        detail.rip?.length > 0;

    useEffect(() => {
        if (selectedDate) renderComponentByDate(selectedDate);
    }, [selectedDate]);

    const renderCalendarHeader = () => (
        <>
            {/* ===== Desktop & Tablet ===== */}
            <div className="hidden lg:flex flex-row justify-center items-center gap-3">
                {days.map((date, index) => {
                    const isToday = date.isSame(baseDate, "day");
                    const isSelected = date.isSame(selectedDate, "day");

                    return (
                        <div
                            key={index}
                            onClick={() => setSelectedDate(date)}
                            className={`flex flex-col items-center px-2 py-2 rounded-lg cursor-pointer lg:min-w-[80px]
                                        ${isSelected ? "bg-primary text-white font-semibold" : "bg-gray-100 text-gray-700"}
                                        ${isToday && !isSelected ? "ring-1 ring-primary text-primary" : ""}`}
                        >
                            <span className="text-sm lg:text-sm">
                                {SharedService.getWeekDay(date.format("dddd"))}
                            </span>
                            <span className="text-xs">
                                {date.format("DD/MM")}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* ===== tablet and mobile===== */}
            <ScrollShadow hideScrollBar orientation="horizontal" className="lg:hidden w-full overflow-hidden">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-1 py-2 scrollbar-hide">
                    {days.map((date, index) => {
                        const isToday = date.isSame(baseDate, "day");
                        const isSelected = date.isSame(selectedDate, "day");

                        return (
                            <div
                                key={index}
                                ref={isToday ? todayRef : null}
                                onClick={() => setSelectedDate(date)}
                                className={`flex-shrink-0 snap-center flex flex-col items-center px-3 py-2 rounded-lg cursor-pointer min-w-[90px]
                                            ${isSelected ? "bg-primary text-white font-semibold" : "bg-gray-100 text-gray-700"}
                                            ${isToday && !isSelected ? "ring-1 ring-primary text-primary" : ""}`}
                            >
                                <span className="text-sm md:text-xs">
                                    {SharedService.getWeekDay(date.format("dddd"))}
                                </span>
                                <span className="text-xs">
                                    {date.format("DD/MM")}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </ScrollShadow>
        </>
    );

    return (
        <div className="w-full flex flex-col justify-center items-center">
            <div className="flex flex-col justify-center w-full bg-white">
                <div className="p-3 w-full">
                    {renderCalendarHeader()}
                </div>
            </div>
            {isLoading ? (
                <div className="text-center text-gray-500 py-8">
                    Đang tải...
                </div>
            ) : !hasEvents ? (
                <div className="text-center text-gray-500 py-8">
                    Không có sự kiện nào cho ngày này.
                </div>
            ) : (
                <div className="w-full mt-2 flex flex-col justify-center items-center">
                    {detail.description && (
                        <p className="px-10 text-xs text-primary italic line-clamp-5 md:line-clamp-2">{detail.description}</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-2 w-full px-5">
                        <div
                            className={`${detail.masses.length > 0 ? "md:col-span-4 p-3 border-l-3 border-primary pl-3" : "hidden"}`}>
                            {detail.masses && detail.masses.map((item, index) => (
                                <div key={index}>
                                    {item.link ? (
                                        <Link
                                            className="font-semibold text-primary hover:underline block"
                                            dangerouslySetInnerHTML={{__html: item.tenLe}}
                                            href={item.linkType === 'articles' ? SharedService.getPostPath(item) : item.link}
                                            target="_blank"
                                        />
                                    ) : (
                                        <h3
                                            className="font-semibold text-gray-800"
                                            dangerouslySetInnerHTML={{__html: item.tenLe}}
                                        />
                                    )}
                                    {item.description && (
                                        <p className="text-xs text-gray-600 italic mt-1">{item.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div
                            className={`${detail.rip.length > 0 || detail.birth.length > 0 || detail.priest.length > 0
                            || detail.saint.length > 0 ? "md:col-span-4 p-3 border-l-3 border-primary pl-3" : "hidden"}`}>
                            {/* RIP */}
                            {detail.rip && detail.rip.length > 0 && (
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-purple-900 font-semibold mb-2">
                                        <TbGrave className="w-4 h-4"/>
                                        <span className="text-sm">Ngày mất:</span>
                                    </div>
                                    <p className="text-xs text-purple-700 leading-relaxed">
                                        {detail.rip.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Birthdays */}
                            {detail.birth && detail.birth.length > 0 && (
                                <div className="bg-yellow-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-yellow-900 font-semibold mb-2">
                                        <LiaBirthdayCakeSolid className="w-4 h-4"/>
                                        <span className="text-sm">Mừng sinh nhật:</span>
                                    </div>
                                    <p className="text-xs text-yellow-700 leading-relaxed">
                                        {detail.birth.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Priest Ordination Anniversary */}
                            {detail.priest && detail.priest.length > 0 && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                                        <GiPartyPopper className="w-4 h-4"/>
                                        <span className="text-sm">Kỷ niệm thụ phong Linh Mục:</span>
                                    </div>
                                    <p className="text-xs text-blue-700 leading-relaxed">
                                        {detail.priest.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Saint's Day */}
                            {detail.saint && detail.saint.length > 0 && (
                                <div className="bg-pink-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-pink-900 font-semibold mb-2">
                                        <GiHeartWings className="w-4 h-4"/>
                                        <span className="text-sm">Mừng bổn mạng:</span>
                                    </div>
                                    <p className="text-xs text-pink-700 leading-relaxed">
                                        {detail.saint.join(', ')}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div
                            className={`${detail.scheduleEvents.length > 0 ? "md:col-span-4 p-3 border-l-3 border-primary pl-3" : "hidden"}`}>
                            {detail.scheduleEvents && detail.scheduleEvents.length > 0 && (
                                <div className="bg-orange-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-orange-900 font-semibold mb-2">
                                        <MdEvent className="w-4 h-4"/>
                                        <span className="text-sm">Sự kiện:</span>
                                    </div>
                                    <div className="space-y-2">
                                        {detail.scheduleEvents.map((e, index) => (
                                            <div key={index} className="text-xs text-gray-700 leading-relaxed">
                                                {e.link && e.link.length > 0 ? (
                                                    <Link className="font-semibold text-primary hover:underline"
                                                          href={e.link}>
                                                        {e.name}
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold">{e.name}</span>
                                                )}
                                                {' - '}
                                                {e.entityType === 'organization' ? (
                                                    <Link className="text-primary hover:underline"
                                                          href={`/nha-tho/${e.entityID}`}>
                                                        {e.quotation}
                                                        {e.code !== 'chau_thanh_the' && (
                                                            <span className="text-gray-600">
                                                                {' '}(Từ {SharedService.formatISODate(e.startDate, "HH:mm")} ngày {SharedService.formatISODate(e.startDate, "DD/MM/YYYY")} đến {SharedService.formatISODate(e.endDate, "HH:mm")} ngày {SharedService.formatISODate(e.endDate, "DD/MM/YYYY")})
                                                            </span>
                                                        )}
                                                    </Link>
                                                ) : (
                                                    <span>
                                                        {e.quotation}
                                                        {e.code !== 'chau_thanh_the' && (
                                                            <span className="text-gray-600">
                                                                {' '}(Từ {SharedService.formatISODate(e.startDate, "HH:mm")} ngày {SharedService.formatISODate(e.startDate, "DD/MM/YYYY")} đến {SharedService.formatISODate(e.endDate, "HH:mm")} ngày {SharedService.formatISODate(e.endDate, "DD/MM/YYYY")})
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>)}
        </div>
    );
}
